import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { AdminHead } from "./AdminLayout";
import { ImageUpload, Field } from "./ImageUpload";
import { api, CATEGORIES, errMsg, uploadFile, useAction, useGet } from "../../lib/api";

const EMPTY = { title: "", category: "peternakan", thumbnail_url: "", excerpt: "", content: "" };

const useModules = (quillRef) =>
  useMemo(() => ({
    toolbar: {
      container: [[{ header: [2, 3, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["blockquote", "link", "image"], ["clean"]],
      handlers: {
        image: () => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            try {
              const url = await uploadFile(file);
              const q = quillRef.current.getEditor();
              const range = q.getSelection(true);
              q.insertEmbed(range.index, "image", url);
            } catch (e) {
              toast.error(errMsg(e));
            }
          };
          input.click();
        },
      },
    },
  }), [quillRef]);

export default function ArticleEditor() {
  const { slug } = useParams();
  const nav = useNavigate();
  const quillRef = useRef(null);
  const modules = useModules(quillRef);
  const [f, setF] = useState(slug ? null : EMPTY);
  const { data } = useGet("article", `/articles/${slug || ""}`);
  const act = useAction(["articles", "article", "stats"]);

  useEffect(() => {
    if (slug && data) {
      setF({ title: data.title, category: data.category, thumbnail_url: data.thumbnail_url, excerpt: data.excerpt, content: data.content });
    }
  }, [slug, data]);

  if (!f) return <p>Memuat artikel...</p>;
  const save = async (e) => {
    e.preventDefault();
    if (!f.thumbnail_url) return toast.error("Unggah gambar utama terlebih dahulu");
    const ok = await act(() => (slug ? api.put(`/articles/${data.id}`, f) : api.post("/articles", f)), "Artikel disimpan");
    if (ok) nav("/admin/artikel");
  };

  return (
    <form onSubmit={save} data-testid="article-editor-form">
      <AdminHead title={slug ? "Edit Artikel" : "Tulis Artikel"}>
        <button type="submit" className="btn-forest" data-testid="article-save-button">Simpan Artikel</button>
      </AdminHead>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Field label="Judul"><Input className="h-12 text-lg" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="article-title-input" /></Field>
          <div className="rounded-2xl bg-white" data-testid="article-content-editor">
            <ReactQuill ref={quillRef} theme="snow" value={f.content} onChange={(v) => setF((p) => ({ ...p, content: v }))} modules={modules} />
          </div>
        </div>
        <div className="space-y-5 rounded-3xl border bg-white p-6">
          <Field label="Kategori">
            <div className="space-y-2">
              {CATEGORIES.map((c) => (
                <label key={c.slug} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm ${f.category === c.slug ? "border-[var(--forest)] bg-[var(--cream)]" : ""}`}>
                  <input type="radio" name="category" checked={f.category === c.slug} onChange={() => setF({ ...f, category: c.slug })} className="accent-[var(--forest)]" data-testid={`article-category-${c.slug}`} />
                  {c.label}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Gambar Utama (Thumbnail)"><ImageUpload value={f.thumbnail_url} onChange={(u) => setF({ ...f, thumbnail_url: u })} testid="article-thumbnail-upload" /></Field>
          <Field label="Cuplikan (opsional, otomatis jika kosong)"><Textarea rows={4} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} data-testid="article-excerpt-input" /></Field>
        </div>
      </div>
    </form>
  );
}
